import React, { useState, useEffect } from "react";
import { makeStyles, Box, InputAdornment, IconButton } from "@material-ui/core";
import { connect } from "react-redux";
import { withSnackbar } from "notistack";
import { CopyToClipboard } from "react-copy-to-clipboard";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import Skeleton from "@material-ui/lab/Skeleton";
import API from "../../../utils/axios";
import { setApiKeys } from "../../../store/actions/account";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import Form from "../../common/Form";
import ConfirmDialog from "../../dialogs/ConfirmDialog";

const useStyles = makeStyles(theme => ({
  iconButton: {
    "& svg": {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5)
    },
    "&:hover": {
      backgroundColor: "#EEE"
    }
  },
  // look up later when apikey field is disabled
  textFieldDisabled: {
    cursor: "not-allowed",
    "& *": {
      cursor: "not-allowed"
    }
  }
}));

function APITab({
  enqueueSnackbar,
  apiKeys,
  setApiKeys,
  plan,
  organisationLoaded
}) {
  const classes = useStyles();

  const [inputValues, setInputValues] = useState({
    apiKey: apiKeys[0] && apiKeys[0].id
  });
  const [apiKeysLoaded, setApiKeysLoaded] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (!apiKeys.length && plan.ft_api_integration) {
      API.get("v1/apikeys").then(res => {
        setApiKeys(res.data);
        setApiKeysLoaded(true);
      });
    } else {
      if (organisationLoaded && plan.ft_api_integration !== undefined) {
        setApiKeysLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.ft_api_integration]);

  useEffect(() => {
    // Set api key value
    setInputValues({
      apiKey: apiKeys[0] && apiKeys[0].id
    });
  }, [apiKeys]);

  // Copy success
  const onCopySuccess = () => {
    enqueueSnackbar("Copied to clipboard");
  };

  // Reset api key
  const handleRefresh = () => {
    if (apiKeys[0]) {
      API.patch(`/v1/apikeys/${apiKeys[0].id}`).then(res => {
        const newApiKeys = [{ ...apiKeys[0], id: res.data.accessToken }];
        setApiKeys(newApiKeys);
      });
    }
  };

  const handleFocus = event => event.target.select();

  return (
    <Box
      pt={4}
      display="flex"
      flexDirection="column"
      height="100%"
      style={{ boxSizing: "border-box" }}
    >
      <Box>
        {!apiKeysLoaded ? (
          <>
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} />
          </>
        ) : (
          <Form
            data={[
              [
                {
                  renderInputField: true,
                  label: "API Key",
                  name: "apiKey",
                  type: "text",
                  dataCy: "apiKey",
                  helperText: " ",
                  value:
                    plan && plan.ft_api_integration ? inputValues.apiKey : "",
                  placeholder:
                    plan && plan.ft_api_integration
                      ? ""
                      : "Please upgrade your plan",
                  shrink: true,
                  disabled: !(plan && plan.ft_api_integration),
                  onFocus: e => handleFocus(e),
                  endAdornment: (
                    <InputAdornment position="end">
                      {plan && plan.ft_api_integration ? (
                        <>
                          <OptimizedTooltip title={"Copy to clipboard"}>
                            <CopyToClipboard
                              data-cy="copyToClipboard"
                              text={inputValues.apiKey}
                              onCopy={onCopySuccess}
                              className={classes.iconButton}
                            >
                              <IconButton aria-label="copy api key">
                                <FileCopyIcon />
                              </IconButton>
                            </CopyToClipboard>
                          </OptimizedTooltip>
                          <OptimizedTooltip title={"Reset API key"}>
                            <IconButton
                              aria-label="reset api key"
                              className={classes.iconButton}
                              onClick={() => setConfirmDialogOpen(true)}
                              data-cy="resetApiKey"
                            >
                              <AutorenewIcon />
                            </IconButton>
                          </OptimizedTooltip>
                        </>
                      ) : (
                        ""
                      )}
                    </InputAdornment>
                  )
                }
              ]
            ]}
          />
        )}
      </Box>
      <ConfirmDialog
        open={confirmDialogOpen}
        handleClose={() => setConfirmDialogOpen(false)}
        action={() => handleRefresh()}
        title="Reset API key?"
        body="This action cannot be undone."
        confirmText="Reset"
      />
    </Box>
  );
}

const mapStateToProps = state => ({
  apiKeys: state.account.apiKeys,
  organisationLoaded: !!state.auth.userData.organisationSite,
  plan:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation.plan) ||
    {}
});

export default connect(mapStateToProps, { setApiKeys })(withSnackbar(APITab));
